/**
 * 单个状态（step）的配置
 * - component：对应的 React 页面组件（可选）
 * - on：事件 → 跳转状态（字符串）或动态跳转（函数）
 */
export type StateConfig<TContext, TState extends string, TEvent extends string> = {
    component?: React.ComponentType<any>;
    on?: {
        // 每个 event 对应一个目标状态或函数，返回目标状态
        [E in TEvent]?: TState | ((ctx: TContext) => TState);
    };
};

/**
 * 整个状态机的配置
 * - initial：初始状态
 * - states：所有状态（每个状态对应一个 StateConfig）
 */
export type MachineConfig<TContext, TState extends string, TEvent extends string> = {
    initial: TState;
    states: Record<TState, StateConfig<TContext, TState, TEvent>>;
};

/**
 * step 的类型
 */
export interface StepProps<TContext> {
    /** event 为当前 step 的选项；answer 为可选对象，会推入 context.answers */
    send: (event: any, answer?: Record<string, unknown>) => void;
    context: TContext;
}

export function createStateMachine<TContext, TState extends string, TEvent extends string | 'BACK'>(
    config: MachineConfig<TContext, TState, TEvent>,
    context: TContext,
) {
    // 当前状态
    let current: TState = config.initial;

    // 内置历史栈
    const history: TState[] = [];

    const ANSWER_KEY = '_answer' as const;

    function send(event: TEvent | 'BACK', payload?: Partial<TContext> & { _answer?: Record<string, unknown> }) {
        if (payload) {
            const answerPayload = payload[ANSWER_KEY];
            if (answerPayload != null && Array.isArray((context as any).answers)) {
                (context as any).answers.push(answerPayload);
            }
            const { [ANSWER_KEY]: _, ...rest } = payload;
            Object.assign(context as any, rest);
        }
        // 处理内置返回：退回上一步并移除上一步对应的 answer
        if (event === 'BACK' || event === 'BACK2') {
            if (history.length > 0) {
                current = history.pop()!;
            }
            if (event === 'BACK2') {
                const answers = (context as any).answers;
                if (Array.isArray(answers) && answers.length > 0) {
                    answers.pop();
                }
            }
            return current;
        }

        const stateDef = config.states[current];
        const eventHandler = stateDef.on?.[event];

        if (!eventHandler) return current;

        const next = eventHandler as TState;

        if (!(next in config.states)) return current;

        // 进入新状态之前，把当前状态记录到历史
        history.push(current);

        current = next;

        return current;
    }

    return {
        context,
        get state() {
            return current;
        },
        send,
    };
}
